import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  /** Explicit per-language paths for pages whose slug differs between languages
   *  (e.g. /exclusivo vs /en/exclusive) — the default prefix swap would 404. */
  alternates?: { pt: string; en: string; es: string };
}

export const SEO = ({ title, description, image, type = 'website', alternates }: SEOProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const baseUrl = 'https://carioquesa.com.br';

  // Get current path without language prefix
  let currentPath = location.pathname;
  if (currentPath.startsWith('/en')) {
    currentPath = currentPath.substring(3) || '/';
  } else if (currentPath.startsWith('/es')) {
    currentPath = currentPath.substring(3) || '/';
  }

  // Build URLs for each language
  const ptUrl = `${baseUrl}${alternates ? alternates.pt : currentPath}`;
  const enUrl = alternates
    ? `${baseUrl}${alternates.en}`
    : `${baseUrl}/en${currentPath === '/' ? '' : currentPath}`;
  const esUrl = alternates
    ? `${baseUrl}${alternates.es}`
    : `${baseUrl}/es${currentPath === '/' ? '' : currentPath}`;

  // Get the current URL based on language
  let currentUrl = ptUrl;
  if (i18n.language === 'en') {
    currentUrl = enUrl;
  } else if (i18n.language === 'es') {
    currentUrl = esUrl;
  }

  const rawTitle = title || t('seo.defaultTitle');
  // Always brand sub-page titles, but avoid duplicating if already branded
  const pageTitle = /carioquesa/i.test(rawTitle)
    ? rawTitle
    : `${rawTitle} | Carioquesa`;

  const rawDescription = description || t('seo.defaultDescription');
  // Clamp meta description to ≤160 chars (truncate at word boundary)
  const pageDescription = rawDescription.length <= 160
    ? rawDescription
    : rawDescription.slice(0, 157).replace(/\s+\S*$/, '') + '…';

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={currentUrl} />

      {/* Hreflang tags */}
      <link rel="alternate" hrefLang="pt-br" href={ptUrl} />
      <link rel="alternate" hrefLang="pt" href={ptUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="x-default" href={ptUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:locale" content={i18n.language === 'pt' ? 'pt_BR' : i18n.language === 'es' ? 'es_ES' : 'en_US'} />
      {i18n.language === 'pt' && (
        <>
          <meta property="og:locale:alternate" content="en_US" />
          <meta property="og:locale:alternate" content="es_ES" />
        </>
      )}
      {i18n.language === 'en' && (
        <>
          <meta property="og:locale:alternate" content="pt_BR" />
          <meta property="og:locale:alternate" content="es_ES" />
        </>
      )}
      {i18n.language === 'es' && (
        <>
          <meta property="og:locale:alternate" content="pt_BR" />
          <meta property="og:locale:alternate" content="en_US" />
        </>
      )}
      {image && <meta property="og:image" content={image} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      {image && <meta name="twitter:image" content={image} />}
    </Helmet>
  );
};
